/* eslint-disable no-console */
import { useTranslation } from 'next-i18next';
import React, { Dispatch, SetStateAction, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { themeColors } from 'utils/tailwind/tailwindHelpers';

import {
  useGetApprovalsEntities,
  useGetCropsList,
  useGetFieldsList,
  useGetJobOrderTypes,
  useGetPathTypes,
} from '@/modules/settings/approvals/apiHooks';
import { IApprovals } from '@/modules/settings/approvals/types';
import { InputField, SelectField } from '@/shared/components/atoms';
import InfiniteSelectField from '@/shared/components/atoms/SelectField/InfiniteSelectField';
import { EntityType } from '@/shared/constant/enums';

export const CreateApprovalPath: React.FC<{
  isFarm: boolean;
  setIsFarm: Dispatch<SetStateAction<boolean>>;
}> = ({ isFarm, setIsFarm }) => {
  const { t } = useTranslation('common');
  // when entity is farm show crops else hidden
  const {
    data: entitiesPages,
    isLoading: isLoadingGetEntities,
    fetchNextPage,
  } = useGetApprovalsEntities({
    pageNumber: 1,
    pageSize: 10,
  });

  const entities = entitiesPages?.pages?.flatMap((page) => page.items);

  const methods = useFormContext<IApprovals.ICreateApprovalPathFormSchema>();

  const {
    data: approvalPathTypeDefaultItemsList,
    isSuccess: isSuccessPathTypes,
  } = useGetPathTypes();

  const { data: jobOrderTypesList } = useGetJobOrderTypes();
  const {
    data: cropsList,
    mutate: mutateCropsList,
    isLoading: isCropsListLoading,
  } = useGetCropsList();
  // approvalPathFields
  const {
    data: fieldsList,
    mutate: mutateFieldsList,
    isLoading: isFieldsListLoading,
  } = useGetFieldsList();

  const entityIdWatcher = methods.watch('entityId');
  const cropsIdWatcher = methods.watch('cropsIds');
  // set is farm or not depend on entity type
  useEffect(() => {
    const isEntityFarm =
      entities?.find((item) => entityIdWatcher == item.id)?.entityTypeId ===
      EntityType.FARM;

    setIsFarm(isEntityFarm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entities, entityIdWatcher]);

  const handleGetCrops = () => {
    mutateCropsList({
      entityId: entityIdWatcher,
    });
  };
  const handleGetFieldsList = (type: 'farm' | 'packhouse') => {
    if (type == 'farm') {
      if (cropsIdWatcher?.length)
        mutateFieldsList({
          cropsIds: cropsIdWatcher,
          entityId: entityIdWatcher,
        });
    } else {
      mutateFieldsList({
        entityId: entityIdWatcher,
      });
    }
  };
  const handleEntityChange = () => {
    if (entityIdWatcher) {
      methods.setValue('cropsIds', []);
      methods.setValue('approvalPathFields', []);

      const isEntityFarm =
        entities?.find((item) => entityIdWatcher == item.id)?.entityTypeId ===
        EntityType.FARM;

      if (isEntityFarm) handleGetCrops();
      else handleGetFieldsList('packhouse');
    }
  };

  useEffect(() => {
    if (isSuccessPathTypes) {
      methods.setValue(
        'approvalPathTypeId',
        approvalPathTypeDefaultItemsList?.[0].id
      );
      // methods.trigger('approvalPathTypeId');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccessPathTypes]);

  useEffect(() => {
    if (jobOrderTypesList) {
      methods.setValue(
        'approvalPathJobOrderTypeId',
        jobOrderTypesList?.[0]?.id
      );
      methods.trigger('approvalPathJobOrderTypeId');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobOrderTypesList]);

  useEffect(() => {
    handleEntityChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityIdWatcher]);

  useEffect(() => {
    if (entityIdWatcher) handleGetFieldsList('farm');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cropsIdWatcher]);

  return (
    <>
      <div className='flex w-full justify-between gap-24'>
        <InputField
          id='name'
          required
          {...methods.register('name')}
          label={t('approval-path-name')}
          placeholder={t('approval-path-name')}
          className='w-full !rounded-xl bg-background-transparent'
          borderColor='border-outline-variant'
          textColor='text-text-default'
          width='w-full'
          py='!py-16'
          errorMessage={methods.formState.errors.name?.message}
          error={Boolean(methods.formState.errors.name)}
        />
        <InfiniteSelectField<IApprovals.IEntityItem, true>
          id='entityId'
          isSearchable
          getLabel={(item: IApprovals.IEntityItem) => item?.name}
          getValue={(item: IApprovals.IEntityItem) => item?.id}
          required
          className='!rounded-xl bg-background-transparent'
          borderColor={themeColors().outline.variant}
          py='10px'
          px='0.5rem'
          borderRadius='1rem'
          // TODO: FIX LIST UPON TYPE
          list={entities || []}
          fetchNextPage={fetchNextPage}
          isLoading={isLoadingGetEntities}
          width='w-full'
          label={t('location')}
          placeholder={t('location')}
        />
      </div>
      {/* <div className='flex w-full justify-between gap-24'>
        <SelectField<IApprovals.IJobOrderTypeDetailsSchema, true>
          id='approvalPathJobOrderTypeId'
          isLoading={isJobOrderTypesListLoading}
          isSearchable
          isDisabled
          getLabel={(item: IApprovals.IJobOrderTypeDetailsSchema) => item.name}
          getValue={(item: IApprovals.IJobOrderTypeDetailsSchema) => item.id}
          required
          list={jobOrderTypesList || []}
          width='w-full'
          py='0.375rem'
          label={t('job-type')}
          placeholder={t('job-type')}
        />
        <SelectField
          id='approvalPathTypeId'
          getLabel={(item: IApprovals.IJobOrderTypeDetailsSchema) => item.name}
          getValue={(item: IApprovals.IJobOrderTypeDetailsSchema) => item.id}
          required
          width='w-full'
          py='0.375rem'
          label={t('the-type')}
          placeholder={t('the-type')}
          isDisabled
          list={approvalPathTypeDefaultItemsList}
        />
      </div> */}

      <div className='flex w-full justify-between gap-24'>
        {isFarm && (
          <SelectField
            id='cropsIds'
            getLabel={(item: IApprovals.IJobOrderTypeDetailsSchema) =>
              item.name
            }
            getValue={(item: IApprovals.IJobOrderTypeDetailsSchema) => item.id}
            required={isFarm}
            width='w-full'
            className='!rounded-xl bg-background-transparent'
            borderColor={themeColors().outline.variant}
            py='10px'
            px='0.5rem'
            borderRadius='1rem'
            isLoading={isCropsListLoading}
            label={t('crop')}
            placeholder={t('crop')}
            list={cropsList?.crops || []}
            isMulti
          />
        )}
        <SelectField
          id='approvalPathFields'
          getLabel={(item: IApprovals.IEntityFieldsDetailsSchema) => item.name}
          getValue={(item: IApprovals.IEntityFieldsDetailsSchema) => item.id}
          required
          width='w-full'
          className='!rounded-xl bg-background-transparent'
          borderColor={themeColors().outline.variant}
          py='10px'
          px='0.5rem'
          borderRadius='1rem'
          isLoading={isFieldsListLoading}
          label={t('work-areas')}
          placeholder={t('work-areas')}
          list={fieldsList?.entityFields || []}
          isMulti
          noOptionsMessage={() => t('no-results-message')}
        />
      </div>
      <div className='flex w-full justify-between gap-24'>
        <InputField
          id='notes'
          {...methods.register('notes', {
            setValueAs: (value) => (value?.length ? value : undefined),
          })}
          label={`${t('notes')} (${t('optional')})`}
          placeholder={`${t('notes')} (${t('optional')})`}
          className='w-full !rounded-xl bg-background-transparent'
          borderColor='border-outline-variant'
          textColor='text-text-default'
          width='w-full'
          py='py-9'
          multiline
          errorMessage={methods.formState.errors.notes?.message}
          error={Boolean(methods.formState.errors.notes)}
        />
      </div>
    </>
  );
};
